import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import circle from '@turf/circle';

const AccuracyMapGoogle = ({ map, googleMapsApi }) => {
    const accuracyPolygonsRef = useRef([]); // To store google.maps.Polygon instances

    // Get position data and transform it into GeoJSON features for circles
    const geoJsonFeatures = useSelector(state =>
        Object.values(state.positions.items)
            .filter(position =>
                position && // Ensure position object exists
                typeof position.latitude === 'number' &&
                typeof position.longitude === 'number' &&
                typeof position.accuracy === 'number' &&
                position.accuracy > 0
            )
            .map(position => {
                // turf/circle expects radius in kilometers.
                // Assuming position.accuracy is in meters.
                const radiusInKm = position.accuracy / 1000;
                return circle([position.longitude, position.latitude], radiusInKm, {
                    steps: 32, // Controls the smoothness of the circle, default is 64
                    units: 'kilometers'
                });
                // Each item in this array will be a GeoJSON Feature object containing a Polygon
            })
    );

    // Effect for adding/updating accuracy circles on the map
    useEffect(() => {
        if (!map || !googleMapsApi || !geoJsonFeatures) {
            // If map isn't ready or no features, do nothing or clear existing
            if (accuracyPolygonsRef.current.length > 0) {
                accuracyPolygonsRef.current.forEach(polygon => polygon.setMap(null));
                accuracyPolygonsRef.current = [];
            }
            return;
        }

        // 1. Clear previously drawn polygons from the map
        accuracyPolygonsRef.current.forEach(polygon => polygon.setMap(null));
        accuracyPolygonsRef.current = []; // Reset the ref array

        // 2. Create and add new Google Maps Polygons
        geoJsonFeatures.forEach(feature => {
            if (feature && feature.geometry && feature.geometry.type === 'Polygon' && feature.geometry.coordinates) {
                // Google Maps Polygon path needs an array of LatLngLiterals
                // feature.geometry.coordinates[0] is the outer ring of the polygon
                const paths = feature.geometry.coordinates[0].map(coordPair => ({
                    lat: coordPair[1], // GeoJSON is [lng, lat]
                    lng: coordPair[0]
                }));

                const accuracyPolygon = new googleMapsApi.Polygon({
                    paths: paths,
                    strokeColor: '#3bb2d0',    // Was fill-outline-color
                    strokeOpacity: 0.8,        // Default, adjust as needed
                    strokeWeight: 1,           // Adjust as needed
                    fillColor: '#3bb2d0',      // Was fill-color
                    fillOpacity: 0.25,         // Was fill-opacity
                    map: map                   // Add it to the map
                });
                accuracyPolygonsRef.current.push(accuracyPolygon);
            }
        });

        // Cleanup function for this effect:
        // Runs when geoJsonFeatures changes (before the effect runs again) or when the component unmounts.
        return () => {
            accuracyPolygonsRef.current.forEach(polygon => polygon.setMap(null));
            accuracyPolygonsRef.current = [];
        };
    }, [map, googleMapsApi, geoJsonFeatures]); // Re-run if map, api, or feature data changes

    // Effect for adding the custom refresh control
    useEffect(() => {
        if (!map || !googleMapsApi) {
            return; // Map or API not ready
        }

        const controlPosition = googleMapsApi.ControlPosition.BOTTOM_LEFT;

        // Prevent adding multiple controls if this effect re-runs (e.g., due to parent re-render)
        // by giving the control an ID and checking for its existence.
        const existingControl = document.getElementById('customMapRefreshControl');
        if (existingControl && map.controls[controlPosition].getArray().includes(existingControl.parentElement)) {
            return;
        }

        const refreshControlWrapper = document.createElement('div');
        refreshControlWrapper.style.margin = '10px'; // Mimic Mapbox control group margin

        const refreshButton = document.createElement('button');
        refreshButton.id = 'customMapRefreshControl'; // ID for the button itself
        refreshButton.type = 'button';
        refreshButton.title = 'Refresh page';
        // Apply styles directly or use a CSS class
        refreshButton.style.backgroundColor = '#fff';
        refreshButton.style.border = '1px solid #ccc'; // Standard Google Maps control border
        refreshButton.style.borderRadius = '2px';    // Standard Google Maps control radius
        refreshButton.style.padding = '5px';
        refreshButton.style.cursor = 'pointer';
        refreshButton.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
        refreshButton.innerHTML = `
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="#3bb2d0" stroke-width="2" fill="none" style="display:block;">
              <path d="M23 4v6h-6"></path>
              <path d="M1 20v-6h6"></path>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
        `;

        refreshButton.addEventListener('mouseenter', () => {
            refreshButton.style.backgroundColor = '#f5f5f5';
        });
        refreshButton.addEventListener('mouseleave', () => {
            refreshButton.style.backgroundColor = '#fff';
        });
        refreshButton.addEventListener('click', () => {
            window.location.reload();
        });

        refreshControlWrapper.appendChild(refreshButton);
        map.controls[controlPosition].push(refreshControlWrapper);

        // Cleanup function for the control
        return () => {
            const controlsArray = map.controls[controlPosition];
            for (let i = 0; i < controlsArray.getLength(); i++) {
                if (controlsArray.getAt(i) === refreshControlWrapper) {
                    controlsArray.removeAt(i);
                    break;
                }
            }
        };
    }, [map, googleMapsApi]); // Add control when map and API are ready

    return null; // This component renders nothing itself, it manipulates the map
};

export default AccuracyMapGoogle;